import { useState, useEffect } from 'react';
import { Trophy, Medal, TrendingUp, Users, ChevronDown, Filter } from 'lucide-react';
import apiClient from '../../api/client';

/**
 * Leaderboard component showing user rankings by goal progress
 */
const GoalsLeaderboard = ({ teams = [] }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    period: '',
    team_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadLeaderboard();
  }, [filters]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.period) params.period = filters.period;
      if (filters.team_id) params.team_id = filters.team_id;
      
      const response = await apiClient.get('/api/v1/goals/leaderboard', { params });
      setLeaderboard(response.data.data.leaderboard || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = cents / 100;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-gray-500">{rank}</span>;
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 75) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    if (percent >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (rank === 2) return 'bg-gray-100 text-gray-700 border-gray-300';
    if (rank === 3) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-white text-gray-600 border-gray-200';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900">Leaderboard</h3>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <Filter className="w-4 h-4" />
          Filters
          <ChevronDown className={`w-4 h-4 transition ${showFilters ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-4 bg-gray-50 border-b flex gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Period</label>
            <select
              value={filters.period}
              onChange={(e) => setFilters({ ...filters, period: e.target.value })}
              className="px-3 py-1.5 border rounded text-sm"
            >
              <option value="">All Periods</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          {teams.length > 0 && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Team</label>
              <select
                value={filters.team_id}
                onChange={(e) => setFilters({ ...filters, team_id: e.target.value })}
                className="px-3 py-1.5 border rounded text-sm"
              >
                <option value="">All Teams</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard List */}
      <div className="divide-y">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading leaderboard...</div>
        ) : leaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No ranking data available</p>
            <p className="text-sm">Users need active goals to appear on the leaderboard</p>
          </div>
        ) : (
          leaderboard.map((entry, index) => (
            <div
              key={entry.user_id}
              className={`p-4 flex items-center gap-4 ${index < 3 ? 'bg-gradient-to-r from-yellow-50/50 to-transparent' : ''}`}
            >
              {/* Rank */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${getRankBadgeColor(entry.rank)}`}>
                {getRankIcon(entry.rank)}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{entry.name}</div>
                <div className="text-xs text-gray-500">
                  {entry.goals_count} goal{entry.goals_count !== 1 ? 's' : ''} | 
                  Target: {formatCurrency(entry.total_target_cents)}
                </div>
              </div>

              {/* Progress */}
              <div className="w-32">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Progress</span>
                  <span className={`font-semibold ${entry.progress_percent >= 100 ? 'text-green-600' : 'text-gray-900'}`}>
                    {entry.progress_percent}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${getProgressColor(entry.progress_percent)}`}
                    style={{ width: `${Math.min(entry.progress_percent, 100)}%` }}
                  />
                </div>
              </div>

              {/* Achieved */}
              <div className="text-right w-24">
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(entry.total_achieved_cents)}
                </div>
                <div className="text-xs text-gray-500">achieved</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {leaderboard.length > 0 && (
        <div className="p-3 bg-gray-50 border-t text-center text-xs text-gray-500">
          Ranked by % to goal • Updated in real-time
        </div>
      )}
    </div>
  );
};

export default GoalsLeaderboard;
